import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import { useMatrixClient } from '../../../../context/MatrixContext';
import { useUserPresence } from '../../../../hooks/useUserPresence';
import { generateInitialsAvatar } from '../../../../utils/avatar';
import { getPresenceColor, getPresenceTitle } from '../../../../utils/presenceUtils';
import { REACTION_FILTER_TYPES } from '../../../../constants';
import RightSidebar from '../../../RightSidebar/RightSidebar';
import './ReactionDetails.css';

const ReactionDetails = ({ 
  onClose = () => {},
  onContactClick = () => {},
  allReactions = new Map(), // All reactions for this message
  members = [], // Contact list from room context
}) => {
  const client = useMatrixClient();
  const { getPresence, isOnline } = useUserPresence();
  const [selectedFilter, setSelectedFilter] = useState(REACTION_FILTER_TYPES.ALL);
  
  // Get current user ID
  const currentUserId = client?.getUserId();

  // Create a lookup map for quick member access by user ID
  const memberLookup = useMemo(() => {
    const lookup = new Map();
    members.forEach(member => {
      // Members might have userId, id, or senderId as the key
      const userId = member.userId || member.id || member.senderId;
      if (userId) {
        lookup.set(userId, member);
      }
    });
    return lookup;
  }, [members]);

  // Process all reactions to get unique emojis and total count
  const reactionStats = useMemo(() => {
    const stats = {
      total: 0,
      emojis: new Map(), // emoji -> count
      allReactors: [], // All reactors with their emojis and member info
    };

    allReactions.forEach((reactorList, emojiKey) => {
      stats.emojis.set(emojiKey, reactorList.length);
      stats.total += reactorList.length;
      
      // Add each reactor with their emoji and member info
      reactorList.forEach(reactor => {
        const memberInfo = memberLookup.get(reactor.senderId);
        
        stats.allReactors.push({
          ...reactor,
          emoji: emojiKey,
          // Use member info if available, otherwise fallback to reactor data
          cleanName: memberInfo?.name || reactor.senderName?.split(':')[0].replace('@', '') || reactor.senderId.split(':')[0].replace('@', ''),
          avatarUrl: memberInfo?.avatar || reactor.senderAvatar || generateInitialsAvatar(memberInfo?.name || reactor.senderId),
        });
      });
    });

    return stats;
  }, [allReactions, memberLookup]);

  // Filter reactors based on selected filter
  const filteredReactors = useMemo(() => {
    if (selectedFilter === REACTION_FILTER_TYPES.ALL) {
      return reactionStats.allReactors;
    }
    return reactionStats.allReactors.filter(reactor => reactor.emoji === selectedFilter);
  }, [reactionStats.allReactors, selectedFilter]);

  return (
    <RightSidebar
      isOpen={true}
      onClose={onClose}
      title="Message Reactions"
      className="reaction-details"
    >
      <div className="reaction-details-filters">
        <button
          className={`reaction-filter-button ${selectedFilter === REACTION_FILTER_TYPES.ALL ? 'active' : ''}`}
          onClick={() => setSelectedFilter(REACTION_FILTER_TYPES.ALL)}
        >
          All {reactionStats.total}
        </button>
        {Array.from(reactionStats.emojis.entries()).map(([emojiKey, count]) => (
          <button
            key={emojiKey}
            className={`reaction-filter-button ${selectedFilter === emojiKey ? 'active' : ''}`}
            onClick={() => setSelectedFilter(emojiKey)}
          >
            {emojiKey} {count}
          </button>
        ))}
      </div>
      
      <div className="reaction-details-content">
        <div className="reaction-details-list">
          {filteredReactors.length > 0 ? (
            filteredReactors.map((reactor, index) => (
              <div key={`${reactor.senderId}-${reactor.emoji}-${index}`} className="reaction-details-item" onClick={() => onContactClick(reactor.senderId)}>
                <div className="reaction-details-avatar">
                  <img 
                    src={reactor.avatarUrl} 
                    alt={reactor.cleanName.charAt(0).toUpperCase()}
                    onError={(e) => {
                      // Fallback to initials avatar if image fails to load
                      e.target.src = generateInitialsAvatar(reactor.cleanName);
                    }}
                  />
                  <div 
                    className="reaction-details-presence-indicator" 
                    style={{ backgroundColor: getPresenceColor(getPresence(reactor.senderId), isOnline(reactor.senderId)) }}
                    title={getPresenceTitle(getPresence(reactor.senderId), isOnline(reactor.senderId))}
                  />
                </div>
                <div className="reaction-details-name">
                  <span className="reaction-details-name-text">{reactor.cleanName}</span>
                  {reactor.senderId === currentUserId && (
                    <span className="reaction-details-you-label">(You)</span>
                  )}
                </div>
                <div className="reaction-details-emoji">
                  {reactor.emoji}
                </div>
              </div>
            ))
          ) : (
            <div className="reaction-details-empty">
              No reactions found.
            </div>
          )}
        </div>
      </div>
    </RightSidebar>
  );
};

ReactionDetails.propTypes = {
  emoji: PropTypes.string, // This might not be used anymore since we show all reactions
  reactors: PropTypes.arrayOf(PropTypes.shape({
    senderId: PropTypes.string.isRequired,
    senderName: PropTypes.string,
    senderAvatar: PropTypes.string,
    eventId: PropTypes.string,
  })),
  messageId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onContactClick: PropTypes.func,
  allReactions: PropTypes.instanceOf(Map),
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userId: PropTypes.string,
    senderId: PropTypes.string,
    name: PropTypes.string,
    avatar: PropTypes.string,
  })),
};

export default ReactionDetails;